import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Card, Typography, Table, Button, Modal, message, Space, Tag, InputNumber, Form, Radio } from 'antd';
import examSessionService from '../../services/examSessionService';
import phieuChamService from '../../services/phieuChamService';
import type { ExamSession, PhieuCham, TieuChi, SinhVien } from '../../../../shared/types';
import { ColumnsType } from 'antd/es/table';
import { AuthService } from '../../services/authService';
import chamDiemService from '../../services/chamDiemService';
import './ChamDiemPage.css';

const { Title, Text } = Typography;

const ChamDiemPage: React.FC = () => {
  const { examSessionId } = useParams<{ examSessionId: string }>();
  const [sessionInfo, setSessionInfo] = useState<ExamSession | null>(null);
  const [students, setStudents] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState<any | null>(null);
  const [phieuCham, setPhieuCham] = useState<PhieuCham | null>(null);
  const [tieuChiList, setTieuChiList] = useState<TieuChi[]>([]);
  const [form] = Form.useForm();

  const fetchData = useCallback(async () => {
    if (!examSessionId) return;
    setLoading(true);
    try {
      const sessionRes = await examSessionService.getExamSessionById(parseInt(examSessionId));
      if(sessionRes.success && sessionRes.data) setSessionInfo(sessionRes.data);

      const studentsRes = await examSessionService.getStudentsForSession(parseInt(examSessionId));
      if(studentsRes.success && studentsRes.data) setStudents(studentsRes.data);
    } catch (error) {
      message.error("Lỗi tải dữ liệu chấm điểm.");
    } finally { setLoading(false); }
  }, [examSessionId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleOpenModal = async (student: any) => {
    setSelectedStudent(student);
    if (sessionInfo?.PhieuChamID) {
      const phieuRes = await phieuChamService.getTieuChiList(sessionInfo.PhieuChamID);
      if (phieuRes.success && phieuRes.data) {
        setTieuChiList(phieuRes.data);
        form.resetFields();
      }
    }
    setIsModalVisible(true);
  };
  
  const handleSaveGrade = async () => {
    try {
      const values = await form.validateFields();
      const chiTiet = Object.keys(values).map(key => ({
        TieuChiID: parseInt(key.replace('diem_', ''), 10),
        DiemThucTe: values[key]
      }));
      
      if (!sessionInfo?.PhieuChamID) {
        message.error("Không tìm thấy phiếu chấm cho kỳ thi này");
        return;
      }
      
      const payload = {
        MaHS: selectedStudent.MaHS,
        PhieuID: sessionInfo.PhieuChamID,
        NguoiCham: AuthService.getUser()?.FullName || 'Administrator',
        chiTiet: chiTiet
      };

      await chamDiemService.saveGrade(payload);
      message.success("Lưu điểm thành công!");
      setIsModalVisible(false);
      fetchData();
    } catch (error) {
      message.error("Lỗi khi lưu điểm.");
    }
  };

  const generateDiemOptions = (maxDiem: number) => {
    const options = [];
    for (let i = 0; i <= maxDiem / 0.25; i++) {
      const value = i * 0.25;
      options.push(<Radio.Button key={value} value={value}>{value}</Radio.Button>);
    }
    return options;
  };

  const groupedTieuChi = tieuChiList.reduce((acc, tieuChi) => {
    const groupName = tieuChi.NoiDung || 'Tiêu chí chung';
    if (!acc[groupName]) {
      acc[groupName] = [];
    }
    acc[groupName].push(tieuChi);
    return acc;
  }, {} as Record<string, TieuChi[]>);

  const columns: ColumnsType<any> = [
    { title: 'Mã SV', dataIndex: 'MaHS' },
    { title: 'Tên sinh viên', dataIndex: 'Hoten' },
    { title: 'Lớp', dataIndex: 'TenLop' },
    {
      title: 'Trạng thái (Bạn)',
      dataIndex: 'CurrentUserDaCham',
      render: (currentUserDaCham) => currentUserDaCham ? <Tag color="green">Đã chấm</Tag> : <Tag color="default">Chưa chấm</Tag>,
    },
    {
      title: 'Tổng lượt chấm',
      dataIndex: 'TongLuotCham',
    },
    {
      title: 'Thao tác',
      render: (_, record) => (
        <Button 
          type="primary" 
          onClick={() => handleOpenModal(record)}
          ghost={record.CurrentUserDaCham}
        >
          {record.CurrentUserDaCham ? 'Chấm lại' : 'Chấm điểm'}
        </Button>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Card>
        <Title level={2}>{`Chấm điểm - ${sessionInfo?.TenKyThi || ''}`}</Title>
        <Table dataSource={students} columns={columns} rowKey="MaHS" loading={loading} />
        <Modal
          title={`Chấm điểm cho ${selectedStudent?.Hoten}`}
          open={isModalVisible}
          onOk={handleSaveGrade}
          onCancel={() => setIsModalVisible(false)}
          width="80%"
          okText="Lưu điểm"
        >
          <Form form={form} layout="vertical" className="grading-form">
            <Text>Người chấm: {AuthService.getUser()?.FullName || 'Administrator'}</Text>
            {Object.entries(groupedTieuChi).map(([group, tieuChis]) => (
              <div key={group} className="grading-group">
                <Title level={4}>{group}</Title>
                {tieuChis.map(tieuChi => (
                  <Form.Item key={tieuChi.TieuChiID} label={tieuChi.TieuChi} className="grading-criterion">
                    <Form.Item name={`diem_${tieuChi.TieuChiID}`} noStyle rules={[{ required: true, message: 'Vui lòng chọn điểm' }]}>
                      <Radio.Group buttonStyle="solid">
                        {generateDiemOptions(tieuChi.Diem)}
                      </Radio.Group>
                    </Form.Item>
                  </Form.Item>
                ))}
              </div>
            ))}
          </Form>
        </Modal>
      </Card>
    </div>
  );
};

export default ChamDiemPage; 